/**
 * Shader Node — Runtime Starter Templates
 *
 * Each runtime mode has a "hello world" template that demonstrates
 * the basic structure and access to the `genie` runtime bridge.
 */

import type { RuntimeMode } from './types';

// ---------------------------------------------------------------------------
// p5.js Template
// ---------------------------------------------------------------------------

const P5_TEMPLATE = `// p5.js sketch — genie runtime available as \`genie\`
// @param float speed 0 5 0.5 Animation speed
// @param float radius 10 300 100 Circle radius

function setup() {
  createCanvas(genie.width, genie.height);
  colorMode(HSB, 360, 100, 100, 100);
}

function draw() {
  background(0, 0, 10);

  const speed = genie.params.speed || 0.5;
  const radius = genie.params.radius || 100;
  const t = frameCount * speed * 0.02;

  // Draw pulsing ring
  noFill();
  for (let i = 0; i < 8; i++) {
    const hue = (frameCount * speed + i * 45) % 360;
    stroke(hue, 80, 90, 60);
    strokeWeight(2);
    const r = radius + sin(t + i * 0.5) * 30;
    ellipse(width / 2, height / 2, r * 2, r * 2);
  }

  // A connected image arrives as a data URL in genie.inputImage;
  // load it with loadImage(genie.inputImage, img => ...) and draw it with image().
}
`;

// ---------------------------------------------------------------------------
// GLSL Fragment Shader Template
// ---------------------------------------------------------------------------

const GLSL_TEMPLATE = `precision mediump float;

uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform sampler2D u_image;

// @param float speed 0.0 5.0 1.0 Animation speed
// @param float intensity 0.0 1.0 0.5 Effect intensity

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  float t = u_time * 1.0; // modified by speed param at runtime

  // Gradient with animated sine wave
  float r = 0.5 + 0.5 * sin(t + uv.x * 6.2831);
  float g = 0.5 + 0.5 * sin(t * 0.7 + uv.y * 6.2831);
  float b = 0.5 + 0.5 * sin(t * 1.3 + (uv.x + uv.y) * 3.1415);

  gl_FragColor = vec4(r, g, b, 1.0);
}
`;

// ---------------------------------------------------------------------------
// Three.js Template
// ---------------------------------------------------------------------------

const THREEJS_TEMPLATE = `// Three.js scene — THREE is available globally, genie runtime as \`genie\`
// @param float rotationSpeed 0.0 5.0 1.0 Rotation speed
// @param color color #00ff88 Mesh color

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
  75,
  genie.width / genie.height,
  0.1,
  1000
);
const renderer = new THREE.WebGLRenderer({
  canvas: document.querySelector('canvas'),
  antialias: true,
});
renderer.setSize(genie.width, genie.height);
renderer.setPixelRatio(genie.pixelRatio);

// Geometry
const geometry = new THREE.TorusKnotGeometry(1, 0.3, 128, 32);
const material = new THREE.MeshStandardMaterial({
  color: 0x00ff88,
  metalness: 0.6,
  roughness: 0.3,
});
const mesh = new THREE.Mesh(geometry, material);
scene.add(mesh);

// Lighting
const ambient = new THREE.AmbientLight(0xffffff, 0.4);
scene.add(ambient);
const directional = new THREE.DirectionalLight(0xffffff, 0.8);
directional.position.set(5, 5, 5);
scene.add(directional);

camera.position.z = 4;

// Animation loop
function animate() {
  requestAnimationFrame(animate);

  const speed = genie.params.rotationSpeed || 1.0;
  mesh.rotation.x += 0.005 * speed;
  mesh.rotation.y += 0.01 * speed;

  renderer.render(scene, camera);
}
animate();
`;

// ---------------------------------------------------------------------------
// Raw Canvas2D / WebGL Template
// ---------------------------------------------------------------------------

const RAW_TEMPLATE = `// Raw Canvas2D — direct context access, genie runtime as \`genie\`
// @param int particleCount 10 1000 200 Number of particles
// @param float speed 0.1 5.0 1.0 Particle speed

const canvas = document.querySelector('canvas');
const ctx = canvas.getContext('2d');
canvas.width = genie.width;
canvas.height = genie.height;

// Create particles
const count = genie.params.particleCount || 200;
const particles = [];
for (let i = 0; i < count; i++) {
  particles.push({
    x: Math.random() * canvas.width,
    y: Math.random() * canvas.height,
    vx: (Math.random() - 0.5) * 2,
    vy: (Math.random() - 0.5) * 2,
    size: Math.random() * 3 + 1,
    hue: Math.random() * 360,
  });
}

function draw() {
  requestAnimationFrame(draw);

  // Fade trail
  ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const speed = genie.params.speed || 1.0;

  for (const p of particles) {
    p.x += p.vx * speed;
    p.y += p.vy * speed;
    p.hue = (p.hue + 0.5) % 360;

    // Wrap edges
    if (p.x < 0) p.x = canvas.width;
    if (p.x > canvas.width) p.x = 0;
    if (p.y < 0) p.y = canvas.height;
    if (p.y > canvas.height) p.y = 0;

    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fillStyle = \`hsl(\${p.hue}, 80%, 60%)\`;
    ctx.fill();
  }
}
draw();
`;

// ---------------------------------------------------------------------------
// Template Map
// ---------------------------------------------------------------------------

/** Map of runtime mode to its starter template code */
export const RUNTIME_TEMPLATES: Record<RuntimeMode, string> = {
  p5: P5_TEMPLATE,
  glsl: GLSL_TEMPLATE,
  threejs: THREEJS_TEMPLATE,
  raw: RAW_TEMPLATE,
};

/**
 * Get the starter template for a given runtime mode.
 */
export function getTemplate(mode: RuntimeMode): string {
  return RUNTIME_TEMPLATES[mode];
}
